<script lang="ts">
import type { PluginWithOptions } from 'markdown-it'
import { computed, defineComponent, h, PropType, watch } from 'vue'

import { renderMarkdown } from '../../utils/renderMarkdown'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type LinkAttributes = { matcher?: (href: string, config: any) => boolean, attrs: Record<string, string> }

export default defineComponent({
  name: 'MarkdownBlock',
  props: {
    /**
     * Input text, must be a valid `markdown` string.
     */
    text: { type: String, default: '' },
    /**
     * The root tag of this component, can be any valid `html` tag
     */
    tag: { type: String, default: 'div' },
    /**
     * Renders `markdown` with no wrapper paragraph tags `<p></p>`
     *
     * Use this mode when your markdown doesn't need to represent multiple lines of text.
     */
    inline: { type: Boolean, default: false },
    /**
     * Configures [markdown-it-link-attributes](https://www.npmjs.com/package/markdown-it-link-attributes)
     * with the provided configuration.
     *
     * See [Basic Configuration](https://github.com/crookedneighbor/markdown-it-link-attributes#basic-configuration)
     * for further instructions on how to use this prop
     */
    linksAttributes: {
      type: Object as PropType<LinkAttributes>,
      default () {
        return {
          attrs: {
            target: '_blank',
            rel: 'noopener'
          }
        }
      }
    },
    /**
     * [markdown-it](https://github.com/markdown-it/markdown-it) breaks parameter
     *
     * Convert '\n' in paragraphs into `<br>`
     */
    breaks: { type: Boolean, default: false },
    /**
     * [markdown-it](https://github.com/markdown-it/markdown-it) html parameter
     *
     * Enable HTML tags in source `markdown` text
     */
    html: { type: Boolean, default: false },
    /**
     * [markdown-it](https://github.com/markdown-it/markdown-it) langPrefix parameter
     * CSS language prefix for fenced blocks. Can be useful for external highlighters.
     */
    langPrefix: { type: String, default: 'language-' },
    /**
     * [markdown-it](https://github.com/markdown-it/markdown-it) linkify parameter
     *
     * Autoconvert URL-like text to links
     */
    linkify: { type: Boolean, default: false },

    /**
     * An array of objects like `{plugin: PluginWithOptions, config: unknown}` to
     * use with [markdown-it use() function](https://github.com/markdown-it/markdown-it#plugins-load)
     */
    additionalPlugins: {
      type: Array as PropType<{ plugin: PluginWithOptions, config: unknown }[]>,
      default: () => []
    },

    /**
     * [markdown-it](https://github.com/markdown-it/markdown-it) quotes parameter
     *
     * Double + single quotes replacement pairs, when typographer enabled,
     * and smartquotes on. Could be either a String or an Array.
     *
     * For example, you can use `'«»„“'` for Russian, `'„“‚‘'` for German,
     * and `['«\xA0', '\xA0»', '‹\xA0', '\xA0›']` for French (including nbsp).
     */
    quotes: { type: [String, Array] as PropType<string | string[]>, default: '“”‘’' },
    /**
     * [markdown-it](https://github.com/markdown-it/markdown-it) typographer parameter
     *
     * Enable some language-neutral replacement + quotes beautification
     *
     * For the full list of replacements, see [here](https://github.com/markdown-it/markdown-it/blob/master/lib/rules_core/replacements.js)
     */
    typographer: { type: Boolean, default: true },
    /**
     * [markdown-it](https://github.com/markdown-it/markdown-it) xhtmlOut parameter
     *
     * Use '/' to close single tags (`<br />`).
     */
    xhtmlOut: { type: Boolean, default: false }
  },
  emits: [
    /**
     * Fires when rendered text is updated
     * @property {string} value Rendered text
     */
    'change'
  ],
  setup (props, { expose, emit }) {
    const html = computed(() => renderMarkdown(props.text, {
      linksAttributes: props.linksAttributes,
      breaks: props.breaks,
      html: props.html,
      langPrefix: props.langPrefix,
      linkify: props.linkify,
      inline: props.inline,
      additionalPlugins: props.additionalPlugins,
      quotes: props.quotes,
      typographer: props.typographer,
      xhtmlOut: props.xhtmlOut
    }))

    watch(html, (value) => emit('change', value), { immediate: true })
    expose({ html })

    return () => h(props.tag, { innerHTML: html.value })
  }
})
</script>
